// TOREFACTO : Use new layout components
import {
  Flex,
  HStack,
  KdsButton,
  KdsLink,
  KdsText,
  KdsTooltip,
  VStack,
} from "@kazaar-platform/design-system";
import { RiExpandDiagonal2Line } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { sumBy } from "lodash";
import { useRef } from "react";

import { getDispatchAddressQuantityForVersion } from "@/helpers/getDispatchAddressQuantityForVersion";
import { getUniqueAddressesFromOrderItem } from "@/helpers/getUniqueAddressesFromOrderItem";
import { useIsHorizontallyScrollable } from "@/hooks";

import { type DeliverySummaryTableVersionsProps } from "./DeliverySummaryTableVersions.types";

export const DeliverySummaryTableVersions = ({
  orderItem,
  onTableVersionExpand,
}: DeliverySummaryTableVersionsProps) => {
  const { t } = useTranslate();

  const versions = orderItem.sandboxItem.SandboxItemVersion;
  const uniqueAddresses = getUniqueAddressesFromOrderItem(orderItem);

  const addressesAreaRef = useRef<HTMLDivElement>(null);

  const totalQuantity = sumBy(versions, (version) =>
    sumBy(version.SandboxItemVersionDispatch, "quantity")
  );

  const isAddressesScrollable = useIsHorizontallyScrollable(addressesAreaRef, [
    uniqueAddresses,
  ]);

  return (
    <VStack gap="sp-xs">
      {/* Table Versions */}
      <div className="flex w-full overflow-hidden">
        {/* Version Column */}
        <div className="min-w-min max-w-fit border-r-bw-small border-outlined-border">
          <div className="flex h-si-xl items-center self-stretch rounded-tl-lg bg-surface-second px-sp-md">
            <KdsText size="subtitle-xs" color="text-bodyvariant">
              {t("tableVersions.headerVersion")}
            </KdsText>
          </div>
          <div className="flex h-si-xl items-center gap-sp-xs self-stretch border-b-bw-small border-outlined-border bg-surface-third px-sp-md">
            <KdsText
              size="subtitle-sm"
              color="text-bodyvariant"
              truncate
              dataCy="tableVersionTotalQuantity"
            >
              {t("tableVersions.totalQuantity", {
                quantity: totalQuantity,
              })}
            </KdsText>
          </div>
          {versions.map((version) => (
            <div
              key={version.id}
              className="flex h-si-xl items-center gap-sp-xs self-stretch border-b-bw-small border-outlined-border bg-white px-sp-md"
            >
              <KdsText size="subtitle-sm">{version.name}</KdsText>
            </div>
          ))}
        </div>

        {/* Addresses Columns */}
        <div
          ref={addressesAreaRef}
          className="flex min-w-0 flex-1 overflow-x-auto"
        >
          {/* Empty column to manage right align */}
          <div className="min-w-0 flex-1">
            <div className="h-si-xl bg-surface-second px-sp-md" />
            <div className="h-si-xl border-y-bw-small border-outlined-border bg-surface-third px-sp-md" />
            {versions.map((version) => (
              <div
                key={version.id}
                className="h-si-xl border-b-bw-small border-outlined-border bg-white px-sp-md"
              />
            ))}
          </div>

          <Flex direction="column">
            <Flex>
              {/* Addresses  */}
              {uniqueAddresses?.map((address, index) => (
                <div
                  key={address.id}
                  className="min-w-[102px] flex-1 overflow-hidden last:rounded-r-lg"
                >
                  <div className="flex h-si-xl items-center justify-center self-stretch bg-surface-second px-sp-md">
                    <KdsTooltip alt={address.nameAddress} placement="top">
                      <KdsLink isUnderline={false}>
                        {address.nameAddress}
                      </KdsLink>
                    </KdsTooltip>
                  </div>
                  <div className="flex h-si-xl items-center justify-end self-stretch border-y-bw-small border-outlined-border bg-surface-third px-sp-md">
                    <KdsText
                      size="caption-sm"
                      color="text-bodyvariant"
                      dataCy={`tableVersionAddress${index + 1}TotalQuantity`}
                    >
                      {address.quantity}
                    </KdsText>
                  </div>
                  {versions.map((version) => {
                    return (
                      <div
                        key={version.id}
                        className="h-si-xl border-b-bw-small border-outlined-border bg-white px-sp-md"
                      >
                        <HStack h="100%" align="center" justify="flex-end">
                          <KdsText>
                            {getDispatchAddressQuantityForVersion(
                              version,
                              address
                            )}
                          </KdsText>
                        </HStack>
                      </div>
                    );
                  })}
                </div>
              ))}
            </Flex>
          </Flex>
        </div>
      </div>

      {/* Expand Button */}
      {onTableVersionExpand && isAddressesScrollable && (
        <KdsButton
          size="small"
          variant="outlined"
          color="neutral"
          iconLeft={RiExpandDiagonal2Line}
          onPress={onTableVersionExpand}
        >
          {t("buttons.expand")}
        </KdsButton>
      )}
    </VStack>
  );
};
